import {
  FilmStrip,
  FolderSimplePlus,
  UploadSimple,
  UserCircle,
} from '@phosphor-icons/react';
import { useListMedia } from '@reelplicate/api-client/media';
import { useListPersonas } from '@reelplicate/api-client/personas';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  CommandPalette,
  type CommandPaletteGroup,
  type CommandPaletteItem,
  useCommandPalette,
} from '@reelplicate/ui';
import { useNavigate } from 'react-router';

import { formatAgo } from '@/features/media/library';
import type { MediaIntent } from '@/features/media/media-layout';
import { SETTINGS_SECTIONS } from '@/features/settings/sections';

import type { NavLinkItem } from './app-layout';

const DAY_MS = 86_400_000;

/** "Generated · 24s · added 2d ago" */
function reelDescription(source: string, seconds: number | null, createdAt: string) {
  const days = Math.max(
    0,
    Math.floor((Date.now() - new Date(createdAt).getTime()) / DAY_MS),
  );
  const ago = formatAgo(days);
  return [
    source.charAt(0) + source.slice(1).toLowerCase(),
    seconds !== null && `${seconds}s`,
    days === 0 ? 'added today' : `added ${ago} ago`,
  ]
    .filter(Boolean)
    .join(' · ');
}

const words = (text: string) =>
  text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);

/**
 * Each query word starts a word of the label, description or keywords
 * ("gen cap" → "Generate captions"). cmdk's default fuzzy match is too loose
 * here: with Actions listed first, "calen" would pick "New collection"
 * (c·reate a·te fo·l·d·e·r campaig·n) over Calendar.
 */
function matchWords(item: CommandPaletteItem, query: string) {
  const haystack = words(
    [item.label, item.description ?? '', ...(item.keywords ?? [])].join(' '),
  );
  return words(query).every((q) => haystack.some((w) => w.startsWith(q)));
}

/**
 * ⌘K / Ctrl+K anywhere in the signed-in app (Figma "Command Palette", 99:2):
 * actions, the workspace's reels and personas, and every page. Reels and
 * personas are fetched only once the palette has been opened.
 */
export function AppCommandPalette({ pages }: { pages: NavLinkItem[] }) {
  const palette = useCommandPalette();
  const navigate = useNavigate();
  const { data: workspaces } = useListWorkspaces();
  const workspaceId = workspaces?.[0]?.id ?? '';
  const enabled = { query: { enabled: Boolean(workspaceId) && palette.open } };
  const { data: library } = useListMedia(workspaceId, enabled);
  const { data: personas } = useListPersonas(workspaceId, enabled);

  const toMedia = (mediaIntent: MediaIntent['mediaIntent']) => () =>
    void navigate('/media', { state: { mediaIntent } satisfies MediaIntent });

  const groups: CommandPaletteGroup[] = [
    {
      heading: 'Actions',
      items: [
        {
          value: 'action:upload',
          label: 'Upload media',
          icon: UploadSimple,
          keywords: ['video', 'image', 'audio', 'file', 'add'],
          onSelect: toMedia('upload'),
        },
        {
          value: 'action:new-collection',
          label: 'New collection',
          icon: FolderSimplePlus,
          keywords: ['create', 'folder', 'campaign'],
          onSelect: toMedia('new-collection'),
        },
      ],
    },
    {
      heading: 'Reels',
      items: (library?.items ?? [])
        .filter((asset) => asset.kind === 'REEL')
        .map((reel) => ({
          value: `reel:${reel.id}`,
          label: reel.title,
          description: reelDescription(reel.source, reel.durationSeconds, reel.createdAt),
          icon: FilmStrip,
          keywords: reel.tags,
          onSelect: () => void navigate(`/media?asset=${encodeURIComponent(reel.id)}`),
        })),
    },
    {
      heading: 'Personas',
      items: (personas?.items ?? []).map((persona) => ({
        value: `persona:${persona.id}`,
        label: persona.name,
        description: `${persona.accountCount} ${persona.accountCount === 1 ? 'account' : 'accounts'}`,
        icon: UserCircle,
        keywords: persona.shortName ? [persona.shortName] : undefined,
        onSelect: () => void navigate('/personas'),
      })),
    },
    {
      heading: 'Go to',
      items: [
        ...pages.map(({ to, label, icon }) => ({
          value: `page:${to}`,
          label,
          icon,
          onSelect: () => void navigate(to),
        })),
        ...SETTINGS_SECTIONS.map(({ path, title, description, icon }) => ({
          value: `page:/settings/${path}`,
          label: title,
          description: 'Settings',
          icon,
          keywords: [description],
          onSelect: () => void navigate(`/settings/${path}`),
        })),
      ],
    },
  ];

  return (
    <CommandPalette
      open={palette.open}
      onOpenChange={palette.setOpen}
      groups={groups}
      filter={matchWords}
      emptyHint="Try a reel name, a persona, a page, or an action like “upload”."
    />
  );
}
